import { EPSILON, MACHINE_EPSILON, ABSCISSAS, WEIGHTS } from './constant';
export function solveCubic(a, b, c, d, _roots, _min, _max) {
    const f = getNormalizationFactor(Math.abs(a), Math.abs(b), Math.abs(c), Math.abs(d));
    let x, b1, c2, qd, q;
    if (f != 0) {
        a *= f;
        b *= f;
        c *= f;
        d *= f;
    }
    function evaluate(x0) {
        x = x0;
        // Evaluate q, q', b1 and c2 at x
        const tmp = a * x;
        b1 = tmp + b;
        c2 = b1 * x + c;
        qd = (tmp + b1) * x + c2;
        q = c2 * x + d;
    }
    // If a or d is zero, we only need to solve a quadratic, so we set
    // the coefficients appropriately.
    if (Math.abs(a) < EPSILON) {
        a = b;
        b1 = c;
        c2 = d;
        x = Infinity;
    }
    else if (Math.abs(d) < EPSILON) {
        b1 = b;
        c2 = c;
        x = 0;
    }
    else {
        // Here onwards we iterate for the leftmost root. Proceed to
        // deflate the cubic into a quadratic (as a side effect to the
        // iteration) and solve the quadratic.
        x = -(b / a) / 3;
        // Evaluate q, q', b1 and c2 at x
        const tmp = a * x;
        b1 = tmp + b;
        c2 = b1 * x + c;
        qd = (tmp + b1) * x + c2;
        q = c2 * x + d;
        // Get a good initial approximation.
        const t = q / a, r = Math.pow(Math.abs(t), 1 / 3), s = t < 0 ? -1 : 1, td = -qd / a, 
        // See Kahan's notes on why 1.324718*... works.
        rd = td > 0 ? 1.324717957244746 * Math.max(r, Math.sqrt(td)) : r;
        let x0 = x - s * rd;
        if (x0 != x) {
            do {
                evaluate(x0);
                // Newton's. Divide by 1 + MACHINE_EPSILON (1.000...002)
                // to avoid x0 crossing over a root.
                x0 = qd == 0 ? x : x - q / qd / (1 + MACHINE_EPSILON);
            } while (s * x0 > s * x);
            // Adjust the coefficients for the quadratic.
            if (Math.abs(a) * x * x > Math.abs(d / x)) {
                c2 = -d / x;
                b1 = (c2 - c) / x;
            }
        }
    }
    // The cubic has been deflated to a quadratic.
    let count = solveQuadratic(a, b1, c2, _roots, _min, _max);
    const boundless = _min === undefined;
    if (isFinite(x) &&
        (count == 0 || count > 0 && (_roots.length > 0 && x != _roots[0]) && (_roots.length > 1 && x != _roots[1])) &&
        (boundless || x > _min - EPSILON && x < _max + EPSILON)) {
        count++;
        _roots.push(boundless ? x : clamp(x, _min, _max));
    }
    return count;
}
function getNormalizationFactor(a, b, c, d) {
    const norm = d ? Math.max(a, b, c, d) : Math.max(a, b, c);
    return norm < 1e-8 || norm > 1e8 ? Math.pow(2, -Math.round(Math.log(norm))) : 0;
}
export function solveQuadratic(a, b, c, roots, min, max) {
    let x1, x2 = Infinity;
    if (Math.abs(a) < EPSILON) {
        // This could just be a linear equation
        if (Math.abs(b) < EPSILON)
            return Math.abs(c) < EPSILON ? -1 : 0;
        x1 = -c / b;
    }
    else {
        // a, b, c are expected to be the coefficients of the equation:
        // Ax² - 2Bx + C == 0, so we take b = -b/2:
        b *= -0.5;
        let D = getDiscriminant(a, b, c);
        // If the discriminant is very small, we can try to normalize
        // the coefficients, so that we may get better accuracy.
        if (D && Math.abs(D) < MACHINE_EPSILON) {
            const f = getNormalizationFactor(Math.abs(a), Math.abs(b), Math.abs(c));
            if (f) {
                a *= f;
                b *= f;
                c *= f;
                D = getDiscriminant(a, b, c);
            }
        }
        if (D >= -MACHINE_EPSILON) { // No real roots if D < 0
            const Q = D < 0 ? 0 : Math.sqrt(D), R = b + (b < 0 ? -Q : Q);
            // Try to minimize floating point noise.
            if (R === 0) {
                x1 = c / a;
                x2 = -x1;
            }
            else {
                x1 = R / a;
                x2 = c / R;
            }
        }
    }
    let count = 0;
    const boundless = min === undefined;
    // We need to include EPSILON in the comparisons with min / max,
    // as some solutions are ever so lightly out of bounds.
    if (x1 && isFinite(x1) && (boundless || (min && x1 > min - EPSILON) && (max && x1 < max + EPSILON)))
        roots[count++] = (min && max) && clamp(x1, min, max) || x1;
    if (x2 !== x1 && isFinite(x2) && (boundless || (min && x2 > min - EPSILON) && (max && x2 < max + EPSILON)))
        roots[count++] = (min && max) && clamp(x2, min, max) || x2;
    return count;
}
function getDiscriminant(a, b, c) {
    // d = b^2 - a * c  computed accurately enough by a tricky scheme.
    // Ported from @hkrish's polysolve.c
    function split(v) {
        const x = v * 134217729, y = v - x, hi = y + x, // Don't optimize y away!
        lo = v - hi;
        return [hi, lo];
    }
    let D = b * b - a * c;
    const E = b * b + a * c;
    if (Math.abs(D) * 3 < E) {
        const ad = split(a), bd = split(b), cd = split(c);
        const p = b * b, dp = (bd[0] * bd[0] - p + 2 * bd[0] * bd[1]) + bd[1] * bd[1], q = a * c, dq = (ad[0] * cd[0] - q + ad[0] * cd[1] + ad[1] * cd[0]) + ad[1] * cd[1];
        D = (p - q) + (dp - dq); // Don’t omit parentheses!
    }
    return D;
}
export function findRoot(f, df, x, a, b, n, tolerance) {
    for (let i = 0; i < n; i++) {
        const fx = f(x), 
        // Calculate a new candidate with the Newton-Raphson method.
        dx = fx / df(x), nx = x - dx;
        // See if we can trust the Newton-Raphson result. If not we use
        // bisection to find another candidate for Newton's method.
        if (Math.abs(dx) < tolerance) {
            x = nx;
            break;
        }
        // Update the root-bounding interval and test for containment of
        // the candidate. If candidate is outside the root-bounding
        // interval, use bisection instead.
        // There is no need to compare to lower / upper because the
        // tangent line has positive slope, guaranteeing that the x-axis
        // intercept is larger than lower / smaller than upper.
        if (fx > 0) {
            b = x;
            x = nx <= a ? (a + b) * 0.5 : nx;
        }
        else {
            a = x;
            x = nx >= b ? (a + b) * 0.5 : nx;
        }
    }
    // Return the best result even though we haven't gotten close
    // enough to the root... (In paper.js this never seems to happen).
    // But make sure, that it actually is within the given range [a, b]
    return clamp(x, a, b);
}
function clamp(value, min, max) {
    return value < min ? min : value > max ? max : value;
}
export function integrate(f, a, b, n) {
    const x = ABSCISSAS[n - 2];
    const w = WEIGHTS[n - 2];
    const A = (b - a) * 0.5;
    const B = A + a;
    let i = 0;
    const m = (n + 1) >> 1;
    // TODO
    let sum = (n & 1) == 0 ? 0 : w[i++] * f(B); // Handle odd n
    while (i < m) {
        const Ax = A * x[i];
        sum += w[i++] * (f(B + Ax) + f(B - Ax));
    }
    return A * sum;
}
//# sourceMappingURL=numerical.js.map